import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, map, of, tap } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';

export interface AppNotification {
  id: string;
  title: string;
  body: string;
  at: string;
  link?: string;
  read?: boolean;
}

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly key = 'atc_notifications_v1';
  readonly items = signal<AppNotification[]>(this.read());
  readonly unreadCount = signal(this.items().filter((n) => !n.read).length);

  load(): void {
    const user = this.auth.currentUser();
    if (!user?.UsrId) {
      this.refreshUnread();
      return;
    }

    this.api
      .get<any[]>(`/Notifications/${user.UsrId}`)
      .pipe(
        map((rows) =>
          (Array.isArray(rows) ? rows : []).map((r) => ({
            id: String(r.Id ?? r.id ?? `n-${Date.now()}`),
            title: String(r.Title ?? r.title ?? ''),
            body: String(r.Body ?? r.body ?? ''),
            link: r.Link ?? r.link ?? undefined,
            at: String(r.CreatedAtUtc ?? r.createdAtUtc ?? new Date().toISOString()),
            read: !!(r.IsRead ?? r.isRead)
          }))
        ),
        catchError(() => of(this.read())),
        tap((list) => {
          this.items.set(list);
          this.persist(list);
          this.refreshUnread();
        })
      )
      .subscribe();
  }

  push(notification: Omit<AppNotification, 'id' | 'at'> & { id?: string; at?: string }): void {
    const next: AppNotification = {
      id: notification.id || `n-${Date.now()}`,
      title: notification.title,
      body: notification.body,
      link: notification.link,
      at: notification.at || new Date().toISOString(),
      read: false
    };
    const list = [next, ...this.items()].slice(0, 50);
    this.items.set(list);
    this.persist(list);
    this.refreshUnread();

    const user = this.auth.currentUser();
    if (user?.UsrId) {
      this.api
        .post('/Notifications', {
          UserId: user.UsrId,
          Title: next.title,
          Body: next.body,
          Link: next.link || null
        })
        .pipe(catchError(() => of(null)))
        .subscribe();
    }
  }

  markAllRead(): void {
    const list = this.items().map((n) => ({ ...n, read: true }));
    this.items.set(list);
    this.persist(list);
    this.unreadCount.set(0);

    const user = this.auth.currentUser();
    if (user?.UsrId) {
      this.api
        .post(`/Notifications/${user.UsrId}/mark-all-read`, {})
        .pipe(catchError(() => of(null)))
        .subscribe();
    }
  }

  private refreshUnread(): void {
    this.unreadCount.set(this.items().filter((n) => !n.read).length);
  }

  private read(): AppNotification[] {
    if (!isPlatformBrowser(this.platformId)) return [];
    try {
      const raw = localStorage.getItem(this.key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private persist(list: AppNotification[]): void {
    if (!isPlatformBrowser(this.platformId)) return;
    localStorage.setItem(this.key, JSON.stringify(list));
  }
}
